












































































































































































import { DateTime } from "luxon";
import Vue from "vue";
import pluralize from "pluralize";
import { SelectOption } from "../types";
import axios from "axios";
import container from "../services";
import startCase from "lodash/startCase";
import { BugReporter } from "../services/BugReporter";
import { Event } from "@bugsnag/js";
import { localTime } from "../utils";

const weekNumbersAsText = ["first", "second", "third", "fourth", "fifth"];

const DAYS_PER_WEEK = 7;
const MAX_OCCURRENCES = 6;
const RECURRENCE_NONE = "none";
const RECURRENCE_DAILY = "daily";

const END_MODE_NEVER = "never";
const END_MODE_DATE = "date";
const END_MODE_OCCURRENCES = "occurrences";

const DATE_SINGLE = "single-date";
const DATE_RANGE = "date-range";

const TIME_ALL_DAY = "all-day";
const TIME_RANGE = "time-range";

const FINAL_START_HOUR = 23;

const ONE_SECOND_MS = 1000;

const BODY_CLASSES = ["fixed", "w-full"];

type VCalendarDatePayload = Date | { start: Date; end: Date };

interface VCalendarBindings {
  minDate: Date;
  isRange: boolean;
  value: VCalendarDatePayload;
}

const clampStartTime = (moment: DateTime, isAllDay: boolean) => {
  const minimumStartTime = startTime();

  if (moment < minimumStartTime) {
    return minimumStartTime;
  }

  if (!isAllDay) {
    return minimumStartTime > moment ? minimumStartTime : moment;
  }

  if (minimumStartTime.hasSame(moment, "day")) {
    return minimumStartTime;
  }

  return moment.startOf("day");
};

/**
 * Determine an appropriate start time.
 *
 * If it's >= 10pm local time, push off until 12am tomorrow.
 * If it's < 10pm, start the next hour from now. If it's 8:32pm, do 9pm.
 *
 * Consider memoizing
 *
 * @return DateTime
 */
const startTime = (): DateTime => {
  const now = localTime();

  if (now.get("hour") >= FINAL_START_HOUR) {
    // once it's 10:00:00pm, force the start time to be midnight the next day
    return now.plus({ days: 1 }).set({ hour: 0, minute: 0, second: 0 });
  }

  // otherwise set the start time to the next hour
  return now.plus({ hours: 1 }).set({ minute: 0, second: 0 });
};

/**
 * Determine an appropriate end time.
 *
 * Add 1 hour to the initial start time. Check the days, and if the date is different,
 * throw an error.
 */
const endTime = (): DateTime => startTime().plus({ hour: 1 });

export default Vue.extend({
  props: {
    currentDate: {
      type: Object as () => DateTime,
      required: false,
      default: (): DateTime => localTime()
    },
    allowOccurrenceLimit: {
      type: Boolean,
      required: false,
      default: (): boolean => true
    },
    kind: {
      type: String,
      required: false,
      default: (): string => "availability",
      validator: (value: string): boolean =>
        ["availability", "exclusion"].includes(value)
    },
    endpoint: {
      type: String,
      required: false,
      default: (): string => "/ajax/availabilities"
    }
  },

  created() {
    this.$nowTick = setInterval(() => {
      if (new Date().getSeconds() === 0) {
        this.now = localTime();
      }
    }, ONE_SECOND_MS);
  },

  beforeMount() {
    document.body.classList.add(...BODY_CLASSES);

    const potential = this.currentDate.set({
      hour: this.startsAt.hour,
      minute: this.startsAt.minute,
      second: 0,
      millisecond: 0
    });

    this.startsAt = clampStartTime(potential, this.timeMode === TIME_ALL_DAY);
  },

  beforeDestroy() {
    if (this.$nowTick) {
      clearInterval(this.$nowTick);
    }
  },

  watch: {
    isSelectingDateRange(value: boolean) {
      if (value) {
        this.recurrence = RECURRENCE_DAILY;
        this.endOn = null;
        this.occurrences = null;
      } else {
        this.recurrence = RECURRENCE_NONE;
      }
    },

    /**
     * When shifting from a specific time range back to 'all day', the start and
     * end times should be updated to reflect the full day.
     */
    isSelectingTimeRange(value) {
      if (value) {
        return;
      }

      if (startTime().hasSame(this.startsAt, "day")) {
        this.startsAt = startTime();
      } else {
        this.startsAt = this.startsAt.set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0
        });
      }

      this.endsAt = this.startsAt.plus({ day: 1 });
    },

    /**
     * Every minute, 'now' changes. Ensure it always reflects a legit star time.
     */
    now(value) {
      if (value >= this.startsAt) {
        this.startsAt = this.startsAt
          .plus({ hour: 1 })
          .set({ second: 0, minute: 0 });
      }
    },

    startsAt(value) {
      let endsAt = this.endsAt.set({
        year: value.year,
        month: value.month,
        day: value.day
      });

      if (endsAt <= value) {
        if (this.timeMode === TIME_ALL_DAY) {
          endsAt = value
            .plus({ day: 1 })
            .set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
        } else {
          endsAt = value.plus({ hour: 1 });
        }
      }

      this.endsAt = endsAt;
    },

    endMode(value) {
      this.occurrences = null;
      this.endOn = null;
    }
  },

  data() {
    const initialStartsAt = startTime();

    return {
      $nowTick: null as number | null,
      saving: false,
      error: false,

      now: localTime(),
      dateMode: DATE_SINGLE,
      timeMode: TIME_ALL_DAY,

      startsAt: initialStartsAt,
      endsAt: initialStartsAt
        .plus({ day: 1 })
        .set({ hour: 0, minute: 0, second: 0, millisecond: 0 }), // 'all day' is the initial range

      recurrence: RECURRENCE_NONE,
      endMode: END_MODE_NEVER,
      endOn: null as DateTime | null,
      occurrences: null,

      END_MODE_DATE,
      END_MODE_OCCURRENCES
    };
  },

  computed: {
    startTime: {
      get(): string {
        return this.startsAt.toISO();
      },
      set(value: string) {
        this.startsAt = DateTime.fromISO(value);
      }
    },

    endTime: {
      get(): string {
        return this.endsAt.toISO();
      },
      set(value: string) {
        this.endsAt = DateTime.fromISO(value);
      }
    },

    recurUntil: {
      get(): DateTime | null {
        return this.endOn;
      },
      set(value: Date) {
        this.endOn = DateTime.fromJSDate(value);
      }
    },

    startTimeOptions(): SelectOption[] {
      const options: SelectOption[] = [];
      let startHour = 0;

      if (this.startsAt.hasSame(this.now, "day")) {
        startHour = this.now.hour + 1;
      }

      let currently = this.startsAt.set({
        hour: startHour,
        minute: 0,
        second: 0
      });
      const midnightNextDay = currently.plus({ day: 1 }).set({ hour: 0 });

      while (currently < midnightNextDay) {
        options.push({
          key: currently.toISO(),
          value: currently.toISO(),
          label: currently.toFormat("h:mm a")
        });

        currently = currently.plus({ hour: 1 });
      }

      return options;
    },

    endTimeOptions(): SelectOption[] {
      let currently = this.startsAt;
      const options: SelectOption[] = [];

      while (currently.plus({ hour: 1 }).hasSame(currently, "day")) {
        currently = currently.plus({ hour: 1 });

        options.push({
          key: currently.toISO(),
          value: currently.toISO(),
          label: currently.toFormat("h:mm a")
        });
      }

      // add midnight the next day.
      currently = this.startsAt
        .plus({ day: 1 })
        .set({ hour: 0, minute: 0, second: 0 });

      options.push({
        key: currently.toISO(),
        value: currently.toISO(),
        label: "Midnight"
      });

      return options;
    },

    dateRangeChoices(): SelectOption[] {
      return [
        {
          key: DATE_SINGLE,
          value: DATE_SINGLE,
          label: "Single Date"
        },
        {
          key: DATE_RANGE,
          value: DATE_RANGE,
          label: "Date Range"
        }
      ];
    },

    timeRangeChoices(): SelectOption[] {
      return [
        {
          key: TIME_ALL_DAY,
          value: TIME_ALL_DAY,
          label: "All Day"
        },
        {
          key: TIME_RANGE,
          value: TIME_RANGE,
          label: "Time Range"
        }
      ];
    },

    isSelectingDateRange(): boolean {
      return this.dateMode === DATE_RANGE;
    },

    isSelectingTimeRange(): boolean {
      return this.timeMode === TIME_RANGE;
    },

    /**
     * The props need to change depending on whether the user is trying to specify
     * an end date.
     */
    datePickerBindings(): VCalendarBindings {
      let value: VCalendarDatePayload;

      if (this.isSelectingDateRange) {
        value = {
          start: this.startsAt.toJSDate(),
          end: this.endsAt.toJSDate()
        };
      } else {
        value = this.startsAt.toJSDate();
      }

      return {
        minDate: this.now.toJSDate(),
        isRange: this.isSelectingDateRange,
        value
      };
    },

    isRecurring(): boolean {
      return this.recurrence !== RECURRENCE_NONE;
    },

    recurrenceOptions(): SelectOption[] {
      const weekdayName = this.startsAt.toFormat("cccc");
      const weekInMonth = Math.ceil(
        (this.startsAt.day + this.startsAt.startOf("month").weekday - 1) /
          DAYS_PER_WEEK
      );

      return [
        {
          value: RECURRENCE_NONE,
          key: RECURRENCE_NONE,
          label: "Does not repeat"
        },
        {
          value: RECURRENCE_DAILY,
          key: RECURRENCE_DAILY,
          label: "Daily"
        },
        {
          value: "weekly",
          key: "weekly",
          label: `Weekly on ${weekdayName}`
        },
        {
          value: "day-of-nth-week-of-month",
          key: "day-of-nth-week-of-month",
          label: `Monthly on the ${
            weekNumbersAsText[weekInMonth - 1]
          } ${weekdayName}`
        },
        {
          value: "annually",
          key: "annually",
          label: "Annually"
        },
        {
          value: "weekdays",
          key: "weekdays",
          label: "Every weekday, Monday - Friday"
        }
      ];
    },

    occurrenceOptions(): SelectOption[] {
      const options: SelectOption[] = [];

      for (let i = 2; i <= MAX_OCCURRENCES; i++) {
        options.push({
          value: i,
          key: i,
          label: pluralize("time", i, true)
        });
      }

      return options;
    },

    endModeOptions(): SelectOption[] {
      const options = [
        {
          value: END_MODE_NEVER,
          key: END_MODE_NEVER,
          label: "Never"
        },
        {
          value: END_MODE_DATE,
          key: END_MODE_DATE,
          label: "On a specific date"
        }
      ];

      if (this.allowOccurrenceLimit) {
        options.push({
          value: END_MODE_OCCURRENCES,
          key: END_MODE_OCCURRENCES,
          label: "After a specific # of occurrences"
        });
      }

      return options;
    },

    formattedRecurrence(): string {
      return (this.recurrenceOptions.find(
        option => option.value === this.recurrence
      ) as SelectOption).label;
    },

    formattedStartsAt(): string {
      return [
        this.startsAt.toLocaleString(DateTime.DATE_HUGE),
        this.startsAt.toLocaleString(DateTime.TIME_SIMPLE)
      ].join(", ");
    },

    formattedEndsAt(): string {
      return [
        this.endsAt.toLocaleString(DateTime.DATE_HUGE),
        this.endsAt.toLocaleString(DateTime.TIME_SIMPLE)
      ].join(", ");
    },

    formattedEndOn(): string | null {
      if (this.endOn === null) {
        return null;
      }

      return this.endOn.toLocaleString(DateTime.DATE_MED_WITH_WEEKDAY);
    }
  },

  methods: {
    pluralize,
    startCase,

    updateDates(value: VCalendarDatePayload) {
      let start;

      if (value instanceof Date) {
        start = DateTime.fromJSDate(value);
      } else {
        start = DateTime.fromJSDate(value.start);
      }

      this.startsAt = clampStartTime(start, this.timeMode === TIME_ALL_DAY);

      if (!(value instanceof Date)) {
        this.endOn = DateTime.fromJSDate(value.end);
      }
    },

    close() {
      document.body.classList.remove(...BODY_CLASSES);

      this.$emit("close");
    },

    async save() {
      this.saving = true;
      this.error = false;

      const params = {
        starts_at: this.startsAt.setZone("UTC").toISO(),
        ends_at: this.endsAt.setZone("UTC").toISO(),
        recurrence: this.recurrence,
        ends_on: this.endOn?.toISODate() || null,
        occurrences: this.occurrences
      };

      try {
        const response = await axios.post(this.endpoint, params);

        this.$emit("created", response.data);
        this.close();
      } catch (error) {
        const bugReporter = container.resolve<BugReporter>("bugreporter");

        // report an error, tracking
        bugReporter.notify(error, (event: Event) => {
          event.addMetadata("definition", { kind: this.kind, ...params });
        });

        this.error = true;
        this.saving = false;
      }
    }
  }
});
